/*###########################################################################
// App
//#########################################################################*/

/* CSS
//------------------*/

import "@css/app.css";

/* App
//------------------*/

window.app = {
	disableScroll() {
		document.documentElement.style.overflow = 'hidden'
	},

	enableScroll() {
		document.documentElement.style.overflow = ''
	}
}

/* Components
//------------------*/

import '@js/components/index'

/* Vendor
//------------------*/

import '@js/vendor/index'

/**
 * Alpine JS
 * Best to put it last so that all other potential JS is available
 * when components start getting initialized.
*/
import Alpine from "alpinejs";
import mapController from "@/js/application/_mapController";
import riveController from "@/js/application/_riveController";
import trackerController from "@/js/application/_trackerController";
import trackerMenuController from "@/js/application/_trackerMenuController";
import audioController from "@/js/application/_audioController";
import introModalController from "@/js/application/_introModal";
import attachmentsModalController from "@/js/application/_attachmentsModal";
import congratulationsModalController from "@/js/application/_congratulationsModal";
import contentPageModalController from "@/js/application/_contentPageModal";

// Attach Alpine to window object
window.Alpine = Alpine;

// Create a store for content page modal state
Alpine.store('map', {
	isDragging: false,
	hasDraggedAboveThreshold: false
});

// Create a store for audio control state
Alpine.store('audio', {
	isMuted: true,
	backgroundTrack: null
});

// Create centralized stores for application state
Alpine.store('tracker', {
	discoveredItems: new Set(),

	// Adding methods to manipulate the store
	addItem(itemId) {
		this.discoveredItems.add(itemId);
		// Check completion after adding item
		Alpine.store('completion').checkCompletion();
	},

	hasItem(itemId) {
		return this.discoveredItems.has(itemId);
	},

	getItems() {
		return [...this.discoveredItems];
	},

	getCount() {
		return this.discoveredItems.size;
	},

	clear() {
		this.discoveredItems.clear();
		Alpine.store('completion').isComplete = false;
		Alpine.store('trackerMenu').resetItems();

		// Saved cleared state
		this.saveProgress()
	},

	saveProgress() {
		// Save to cookie with 30 day expiration using the store's getItems method
		const expirationDate = new Date();
		expirationDate.setDate(expirationDate.getDate() + 30);
		document.cookie = `discoveredItems=${encodeURIComponent(JSON.stringify(Alpine.store('tracker').getItems()))};expires=${expirationDate.toUTCString()};path=/`;
	}
});

// Create a store for completion tracking
Alpine.store('completion', {
	isComplete: false,
	totalItems: 0,

	// Method to check if all items are discovered
	checkCompletion() {
		const discoveredCount = Alpine.store('tracker').getCount();

		// Only mark as complete if there are items to find and all are found
		if (this.totalItems > 0 && discoveredCount >= this.totalItems) {
			this.isComplete = true;
		}
	},

	// Method to set the total number of trackable items
	setTotalItems(count) {
		this.totalItems = count;

		// Check completion immediately in case items were already loaded
		this.checkCompletion();
	},

	clear() {
		this.isComplete = false;
		this.totalItems = 0;
	}
});

// Create a store for intro modal state
Alpine.store('introModal', {
	requestOpen: false,

	// Method to request opening the modal
	open() {
		this.requestOpen = true;
	}
});

// Create a store for attachments modal state
Alpine.store('attachmentsModal', {
	requestOpen: false,

	// Method to request opening the modal
	open() {
		this.requestOpen = true;
	}
});

// Create a store for congratulations modal state
Alpine.store('congratulationsModal', {
	requestOpen: false,

	// Method to request opening the modal
	open() {
		this.requestOpen = true;
	}
});

// Create a store for content page modal state
Alpine.store('contentPageModal', {
	requestOpen: false,

	// Method to request opening the modal
	open() {
		this.requestOpen = true;
	}
});

// Create a store for tracker menu state
Alpine.store('trackerMenu', {
	isOpen: false,
	activeDetailPanel: null,
	phase: 1,
	selectedItem: null,

	// Adding methods for better control
	open() {
		this.isOpen = true;
	},

	close() {
		this.isOpen = false;
		this.phase = 1;
		this.selectedItem = null;
	},

	showDetail(panelId) {
		const self = this;

		// Clear any existing timeout to ensure no premature closing
		if (this.closeTimeout) {
			clearTimeout(this.closeTimeout);
		}

		// Open menu and show the detail panel, if not hovered or touched then timeout the menu and close
		//
		this.activeDetailPanel = panelId;
		this.open();
	},

	hideDetail() {
		this.activeDetailPanel = null;
	},

	resetItems() {
		document.querySelectorAll('.tracker-menu__item').forEach(item => {
			item.classList.add('tracker-menu__item--locked');
			const titleSpan = item.querySelector('.item-title');
			if (titleSpan) {
				titleSpan.textContent = '?';
			}
		});
	},

	handleOutsideClick(event) {
		const trackerMenu = document.querySelector('.tracker-menu');
		if (trackerMenu && !trackerMenu.contains(event.target)) {
			Alpine.store('trackerMenu').close();
		}
	}
});

// Register components
Alpine.data('trackerMenu', trackerMenuController);
Alpine.data('audioControl', audioController);
Alpine.data('introModal', introModalController);
Alpine.data('attachmentsModal', attachmentsModalController);
Alpine.data('congratulationsModal', congratulationsModalController);
Alpine.data('contentPageModal', contentPageModalController);

Alpine.data("mapWithRive", (initialWidth, mobileWidth) => {
	return {
		...mapController(initialWidth, mobileWidth), // Map logic
		...riveController(), // Rive logic
		...trackerController(), // Tracker logic
		init() {
			const self = this;

			self.setupMap();
			self.initRiveCanvases();
			self.initTrackerController();
		},
	};
});

Alpine.start();

// console.log('Tracker Store:', Alpine.store('tracker'))
// console.log('Tracker Menu Store:', Alpine.store('trackerMenu'))

/**
 * Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
	import.meta.hot.accept(() => {
		console.log("HMR");
	});
}
